/**
 * ConversationSearch — a noragsearch-inspired search box for the sidebar.
 *
 * Grades every one of the user's conversations against a natural-language
 * query with a cheap LLM (no keywords, no embeddings), ranks them
 * deterministically, and lists the top hits inline. Clicking a hit opens
 * that conversation.
 */

import React, { useCallback, useState } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import {
  searchConversations,
  ConversationSearchResult,
  SearchCorpus,
} from '../../services/conversationSearchService';

/** K3 display order for grade counts (best to worst, then ungraded). */
const GRADE_ORDER = ["A","A/B","A/C","B","A/D","B/C","A/F","B/D","C","B/F","C/D","C/F","D","D/F","F","ungraded"];

interface ConversationSearchProps {
  onSelectConversation: (conversationId: string) => void;
  currentConversationId?: string;
}

const GRADE_COLORS: Record<string, string> = {
  A: 'bg-green-600 text-white',
  B: 'bg-emerald-700 text-white',
  C: 'bg-yellow-600 text-black',
  D: 'bg-orange-700 text-white',
  F: 'bg-red-700 text-white',
};

export const ConversationSearch: React.FC<ConversationSearchProps> = ({
  onSelectConversation,
  currentConversationId,
}) => {
  const [query, setQuery] = useState('');
  const [corpus, setCorpus] = useState<SearchCorpus>('prompts');
  const [maxHits, setMaxHits] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ConversationSearchResult | null>(null);
  const [page, setPage] = useState(0);
  const [pageSort, setPageSort] = useState<'recent' | 'oldest' | 'turns'>('recent');

  // The backend grades the whole corpus and returns the full ranked list, so
  // paging is a free client-side slice (no re-grading). maxHits = page size.
  const pageSize = Math.max(1, maxHits);
  const totalRanked = result?.hits.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRanked / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const start = safePage * pageSize;
  // Always stratified by judgment (better grade first); the dropdown only
  // chooses the order *within* a grade. Sort the full ranked list, then page,
  // so numbering is sequential and page composition reflects the choice.
  const gradeRank = (g: string) => {
    const i = GRADE_ORDER.indexOf(g === '—' ? 'ungraded' : g);
    return i < 0 ? GRADE_ORDER.length : i;
  };
  const sortedHits = result ? [...result.hits].sort((a, b) => {
    const dg = gradeRank(a.grade) - gradeRank(b.grade);
    if (dg !== 0) return dg;
    if (pageSort === 'turns' && a.turns !== b.turns) return b.turns - a.turns;
    const d = a.lastUpdatedAt - b.lastUpdatedAt;
    return pageSort === 'oldest' ? d : -d;
  }) : [];
  const pageHits = sortedHits.slice(start, start + pageSize);

  const runSearch = useCallback(async () => {
    const q = query.trim();
    if (!q || loading) return;
    setLoading(true);
    setError(null);
    try {
      const r = await searchConversations(q, maxHits, corpus);
      setResult(r);
      setPage(0);
    } catch (e: any) {
      setError(e?.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  }, [query, maxHits, corpus, loading]);

  const clear = useCallback(() => {
    setResult(null);
    setPage(0);
    setError(null);
    setQuery('');
  }, []);

  return (
    <div className="px-3 pt-2 pb-1">
      {/* Query box */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') runSearch();
            if (e.key === 'Escape') clear();
          }}
          placeholder="find conversations where we discussed…"
          disabled={loading}
          className="w-full pl-8 pr-8 py-1.5 text-sm rounded-lg bg-muted/60 border border-border focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground"
          aria-label="Search my conversations"
        />
        {(query || result) && !loading && (
          <button
            onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-muted-foreground hover:text-foreground"
            title="Clear search"
            aria-label="Clear search"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        {loading && (
          <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-primary" />
        )}
      </div>

      {/* Controls: corpus toggle + max hits */}
      <div className="flex items-center gap-1.5 mt-1.5 text-[10px]">
        <div className="inline-flex rounded overflow-hidden border border-border">
          <button
            type="button"
            onClick={() => setCorpus('prompts')}
            disabled={loading}
            className={`px-1.5 py-0.5 transition-colors ${
              corpus === 'prompts'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-muted-foreground hover:bg-muted'
            }`}
            title="Grade only my prompts"
          >
            prompts
          </button>
          <button
            type="button"
            onClick={() => setCorpus('full')}
            disabled={loading}
            className={`px-1.5 py-0.5 border-l border-border transition-colors ${
              corpus === 'full'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-muted-foreground hover:bg-muted'
            }`}
            title="Grade prompts and ionoff responses"
          >
            prompts+responses
          </button>
        </div>
        <label className="flex items-center gap-1 text-muted-foreground ml-auto">
          max
          <input
            type="number"
            min={1}
            max={50}
            value={maxHits}
            onChange={(e) => setMaxHits(Math.min(50, Math.max(1, Number(e.target.value) || 1)))}
            disabled={loading}
            className="w-10 px-1 py-0.5 rounded bg-muted/60 border border-border text-foreground text-[10px] focus:outline-none focus:ring-1 focus:ring-primary"
            aria-label="Maximum hits"
          />
        </label>
      </div>

      {error && <p className="text-xs text-red-500 mt-1.5">{error}</p>}

      {/* Results */}
      {result && (
        <div className="mt-2">
          {result.hits.length > 0 && (
            <div className="flex items-center justify-end gap-1 px-1 mb-1 text-[10px] text-muted-foreground">
              <span>page order</span>
              <select
                value={pageSort}
                onChange={(e) => setPageSort(e.target.value as 'recent' | 'oldest' | 'turns')}
                className="bg-background border border-border rounded px-1 py-0.5 text-[10px]"
                title="Order within each grade; results are always best judgment first"
              >
                <option value="recent">recent first</option>
                <option value="oldest">oldest first</option>
                <option value="turns">most turns first</option>
              </select>
            </div>
          )}
          {result.hits.length === 0 ? (
            <div className="text-xs text-muted-foreground text-center py-3">no matches</div>
          ) : (
            <div className="space-y-0.5">
              {pageHits.map((hit, i) => (
                <button
                  key={hit.conversationId}
                  onClick={() => onSelectConversation(hit.conversationId)}
                  className={`w-full text-left flex items-start gap-2 px-2 py-1 rounded-lg transition-colors ${
                    currentConversationId === hit.conversationId
                      ? 'bg-muted'
                      : 'hover:bg-muted/50'
                  }`}
                  title={hit.rationale || hit.title}
                >
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm truncate">
                      <span className="text-muted-foreground mr-1">{start + i + 1}.</span>
                      {hit.title}
                    </span>
                    {hit.rationale && (
                      <span className="block text-[10px] text-muted-foreground line-clamp-2 leading-tight">
                        {hit.rationale}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-1 mt-1 text-[10px] text-muted-foreground">
              <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={safePage === 0} className="px-1.5 py-0.5 rounded hover:bg-muted disabled:opacity-40">‹ prev</button>
              <span>page {safePage + 1} of {totalPages} · {totalRanked} ranked</span>
              <button type="button" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={safePage >= totalPages - 1} className="px-1.5 py-0.5 rounded hover:bg-muted disabled:opacity-40">next ›</button>
            </div>
          )}
          <div className="text-[10px] text-muted-foreground mt-1.5 px-1 leading-tight">
            judged {result.stats.candidates} conversations ·{' '}
            {result.stats.reasonerCalls} call{result.stats.reasonerCalls === 1 ? '' : 's'} ·{' '}
            ${result.stats.costUSD.toFixed(3)} · {(result.stats.durationMs / 1000).toFixed(1)}s
            {result.stats.ungraded > 0 && ` · ${result.stats.ungraded} ungraded`}
          </div>
          {result.stats.interpretation && (
            <div className="text-[10px] text-muted-foreground mt-1 px-1 leading-tight italic" title="How the grader read your request, in its own words">
              read your request as: “{result.stats.interpretation}”
            </div>
          )}
          {result.stats.gradeDistribution && (
            <div className="text-[10px] text-muted-foreground mt-1 px-1 leading-tight" title="How many conversations received each grade; the list above is the top-N by grade, then most recent first">
              judgments: {GRADE_ORDER.filter((g) => result.stats.gradeDistribution![g]).map((g) => `${g} ${result.stats.gradeDistribution![g]}`).join(" · ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
